import React, { useCallback } from 'react';
import { noop } from 'lodash';

import IP from 'constants/form/inputParser.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import parseInteger from 'utils/parsers/parseInteger.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import asInput from 'utils/strings/asInput.util';

import CN from './IntegerInput.module.scss';

const CTAG = 'IntegerInput';

const L = logger(CTAG);

const IntegerInput = ({
  name,
  noNegative,
  onBlur = noop,
  onChange = noop,
  onClick = noop,
  onFocus = noop,
  pad,
  readOnly: isReadOnly,
  tag,
  value: valueFromProps,
  children, // removed from extra, so it is not passed to the <input>
  ...extra
}) => {
  const t = fromAnyToTrimmed(tag);

  const [localValue, setLocalValue] = useStateWhileMounted(valueFromProps);
  const { val: isDirty, inv: isClean, set: setDirty } = useTip(false);
  const { val: isError, inv: isValid, set: setError } = useTip(false);
  const { val: isFocused, inv: isDefocused, yay: gotFocus, nay: lostFocus } = useTip(false);

  const prepare = useCallback(
    (ev) => {
      const value = fromEventToValue(ev);
      const parsed = parseInteger({ value });
      const dirty = value !== localValue;

      return { ...parsed, tag: t, parser: IP.int, name, dirty, event: ev, ev };
    },
    [localValue, name, t],
  );

  const reconcile = useCallback(
    ({ result: { dirty, error, value }, prepared, name }) => {
      const number = name === 'onBlur' ? prepared.string : prepared.raw;
      const newValue = isMissing(value) ? number : value;
      const newDirty = isMissing(dirty) ? newValue !== valueFromProps : dirty;

      setLocalValue(newValue);
      setDirty(newDirty);
      setError(error);
    },
    [setDirty, setError, setLocalValue, valueFromProps],
  );

  const handleAs = useCallback(
    (name, fn, ev) => {
      const prepared = prepare(ev);
      const result = asObject(call(`${CTAG}.${name}`, fn, prepared));
      return reconcile({ name, result, prepared });
    },
    [prepare, reconcile],
  );

  const handleChange = useCallback((ev) => handleAs('onChange', onChange, ev), [handleAs, onChange]);
  const handleClick = useCallback((ev) => handleAs('onClick', onClick, ev), [handleAs, onClick]);

  const handleFocus = useCallback(
    (ev) => {
      gotFocus();
      return handleAs('onFocus', onFocus, ev);
    },
    [gotFocus, handleAs, onFocus],
  );

  const handleBlur = useCallback(
    (ev) => {
      lostFocus();
      handleAs('onBlur', onBlur, ev);
    },
    [handleAs, lostFocus, onBlur],
  );

  const { is, number, string } = parseInteger({ value: valueFromProps, pad, noNegative });
  // to stop React from complaining about NaN being set as a prop, use a string replacement
  const dataAsNumber = fromAnyToString(number);

  // since you can't change it, pretty print it during render, don't wait on blur event
  const displayValue = fromAnyToString((isReadOnly || isDefocused) && !isMissing ? valueFromProps : localValue);
  const minValue = noNegative ? 0 : void 1;

  L.debug('()', t, { displayValue, valueFromProps, localValue });

  return (
    <input
      type="number"
      inputMode="numeric"
      min={minValue}
      autoComplete="off"
      {...extra}
      data-bem="IntegerInput"
      data-tag={t}
      data-field-clean={isClean}
      data-field-dirty={isDirty}
      data-field-error={isError}
      data-field-focus={isFocused}
      data-field-valid={isValid}
      data-is-valid={is.valid}
      data-is-empty={is.empty}
      data-is-int={is.int}
      data-is-float={is.float}
      data-is-positive={is.positive}
      data-is-negative={is.negative}
      data-is-nan={is.nan}
      data-is-infinite={is.infinite}
      data-as-number={dataAsNumber}
      data-as-string={string}
      className={CN.component}
      name={name}
      value={asInput(displayValue)}
      onBlur={handleBlur}
      onChange={handleChange}
      onClick={handleClick}
      onFocus={handleFocus}
    />
  );
};

// noinspection JSUnusedGlobalSymbols
export default IntegerInput;
